import { toast, type ExternalToast } from 'vue-sonner'

export type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info'

interface AppToastOptions extends ExternalToast {
  title?: string
  description?: string
  duration?: number
}

/** Wraps vue-sonner with typed success/error/info/warning helpers. */
export const useAppToast = () => {
  // Shows a toast of the given type with optional description and duration.
  const show = (
    type: ToastType,
    title: string,
    description?: string,
    options?: AppToastOptions
  ) => {
    const toastOptions: ExternalToast = {
      ...options,
      description: description,
      duration: options?.duration || 3000
    }

    switch (type) {
      case 'success':
        return toast.success(title, toastOptions)
      case 'error':
        return toast.error(title, toastOptions)
      case 'warning':
        return toast.warning(title, toastOptions)
      case 'info':
        return toast.info(title, toastOptions)
      default:
        return toast(title, toastOptions)
    }
  }

  return {
    success: (title: string, description?: string, options?: AppToastOptions) =>
      show('success', title, description, options),

    error: (title: string, description?: string, options?: AppToastOptions) =>
      show('error', title, description, options),

    info: (title: string, description?: string, options?: AppToastOptions) =>
      show('info', title, description, options),

    warning: (title: string, description?: string, options?: AppToastOptions) =>
      show('warning', title, description, options),

    default: (title: string, description?: string, options?: AppToastOptions) =>
      show('default', title, description, options)
  }
}
